import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  IconButton,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import { ChevronLeft, Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import api from "../../services/api"
import { API_ENDPOINTS } from "../../config/api"
import { useAuth } from '../../context/AuthContext';

export default function EditUser() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    image_url: '',
    email: '',
    password: '',
    is_active: true,
    role_id: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [loadingUser, setLoadingUser] = useState(true);

  const fetchRoles = useCallback(async () => {
    try {
      const response = await api.get(API_ENDPOINTS.ROLE.GET_ALL);
      setRoles(response.data.roles);
    } catch (err) {
      console.error('Error fetching roles:', err);
      setError('Failed to fetch roles. Please try again.');
    } finally {
      setLoadingRoles(false);
    }
  }, []);

  const fetchUser = useCallback(async () => {
    try {
      const response = await api.get(API_ENDPOINTS.USER.GET_ONE(id));
      const userData = response.data.user;
      setFormData({
        name: userData.name,
        image_url: userData.image_url || '',
        email: userData.email,
        password: '',
        is_active: userData.is_active,
        role_id: userData.role_id.id,
      });
    } catch (err) {
      console.error('Error fetching user:', err);
      setError('Failed to fetch user data. Please try again.');
    } finally {
      setLoadingUser(false);
    }
  }, [id]);

  useEffect(() => {
    fetchRoles();
    fetchUser();
  }, [fetchRoles, fetchUser]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const requestData = { ...formData, client_id: user.clientId };
      if (!requestData.password) {
        delete requestData.password;
      }
      await api.put(API_ENDPOINTS.USER.UPDATE(id), requestData);
      navigate('/users');
    } catch (err) {
      console.error('Error updating user:', err);
      setError('Failed to update user. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  if (loadingRoles || loadingUser) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', pl: 1, pr: 2, pt: 0, backgroundColor: 'background.paper', borderRadius: 8 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton 
          onClick={() => navigate('/users')}
          sx={{ 
            mr: 2, 
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: 'action.hover',
            }
          }}
        >
          <ChevronLeft />
        </IconButton>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Edit User
        </Typography>
      </Box>

      <Paper 
        sx={{ 
          p: 2, 
          borderRadius: 6,
          boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
          backgroundColor: 'background.paper'
        }}
      >
        <Box component="form" onSubmit={handleSubmit}>
          {error && (
            <Typography color="error" sx={{ mb: 2 }}>
              {error}
            </Typography>
          )}
          <Box sx={{ mb: 3 }}>
            <TextField
              required
              fullWidth
              label="Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 20,
                  '& fieldset': {
                    borderColor: '#E0E0E0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#C41747',
                  },
                },
              }}
            />
          </Box>
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Image URL"
              name="image_url"
              value={formData.image_url}
              onChange={handleChange}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 20,
                  '& fieldset': {
                    borderColor: '#E0E0E0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#C41747',
                  },
                },
              }}
            />
          </Box>
          <Box sx={{ mb: 3 }}>
            <TextField
              required
              fullWidth
              type="email"
              label="Email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 20,
                  '& fieldset': {
                    borderColor: '#E0E0E0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#C41747',
                  },
                  '& input:-webkit-autofill': {
                    WebkitBoxShadow: '0 0 0 30px white inset !important',
                    WebkitTextFillColor: '#1E3A5F !important',
                  },
                },
              }}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              type={showPassword ? 'text' : 'password'}
              label="New Password (leave blank to keep current)"
              name="password"
              value={formData.password}
              onChange={handleChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                      sx={{ color: 'text.primary' }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 20,
                  '& fieldset': {
                    borderColor: '#E0E0E0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#C41747',
                  },
                  '& input:-webkit-autofill': {
                    WebkitBoxShadow: '0 0 0 30px white inset !important',
                    WebkitTextFillColor: '#1E3A5F !important',
                  },
                },
              }}
            />
          </Box>

          <Box sx={{ mb: 4 }}>
            <FormControl fullWidth required>
              <InputLabel id="role-label">Role</InputLabel>
              <Select
                labelId="role-label"
                name="role_id"
                value={formData.role_id}
                onChange={handleChange}
                label="Role"
                sx={{
                  borderRadius: 20,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#E0E0E0',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#C41747',
                  },
                  '& .MuiSelect-icon': {
                    color: 'text.primary'
                  }
                }}
              >
                {roles.map((role) => (
                  <MenuItem key={role.id} value={role.id}>{role.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{
                bgcolor: '#E31B54',
                color: '#FFFFFF',
                textTransform: 'none',
                borderRadius: 18,
                px: 4,
                '&:hover': { bgcolor: '#C41747' },
              }}
            >
              {loading ? 'Updating...' : 'Update'}
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/users')}
              sx={{
                color: "#F9826E",
                borderColor: "#F9826E",
                textTransform: "none",
                borderRadius: 18,
                "&:hover": {
                  color: "black",
                  borderColor: "#F7583D",
                  bgcolor: "#F9826E",
                },
              }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
